
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vselect3 from "vselect3";
import { namespace, Getter } from "vuex-class";
import { CancelTokenSource } from "axios";
import { debounce } from "lodash";
import api from "../../services/api.service";
import { VueBootstrapField } from '@/store/types';
import SureViewIcon from '../SureViewIcon.vue';
import { Response } from "@/store/responses/types";
import { AlarmPointHistoryItem, AlarmPointHistoryPagedResponse } from "@/store/alarm-points/types";
import { getNewCancelTokenSource } from "@/utils/cancellableQueryHelper";
import { getDebouncePeriod } from "@/utils/debounce-helper";

const AlarmPoints = namespace("alarmPoints");

@Component({
	components: {
		VuePerfectScrollbar,
		"sureview-icon": SureViewIcon,
		"v-select-3": vselect3,
	}
})
export default class AlarmPointHistory extends Vue {
	private readonly resultsPerPage: number = 50;
	private readonly monthSelectOptionsCount: number = 6;

	private fetchingHistory: boolean = false;
	private historyItems: AlarmPointHistoryItem[] = [];
	private filter: string = "";
	private historyCancelTokenSource: CancelTokenSource | null = null;
	private readonly cancelPreviousHistoryMessage: string = "Cancel Alarm Point History Search";

	private monthOffset: number = 0;
	private selectedPage: number = 1;
	private totalAvailableRows: number = 0;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@AlarmPoints.State("selectedResponse") private response : Response | null;

	private handleFilterChanged: () => void;

	private created(): void {
		this.handleFilterChanged = debounce((): void => {
			this.updateResults(this.response);
			this.selectedPage = 1;
		}, getDebouncePeriod(), { leading: false, trailing: true });
	}

	private async mounted(): Promise<void> {
		if (this.response != null) {
			this.fetchAlarmPointHistory(this.response);
		}
	}

	private get isPaginatedResultsFeatureEnabled(): boolean {
		return this.getFeature(["Alarms", "AlarmPoints", "PaginatedHistoryResults"]);
	}

	private get filteredHistory(): AlarmPointHistoryItem[] {
		if (this.isPaginatedResultsFeatureEnabled || this.filter == "")  {
			return this.historyItems;
		}

		var filterLower = this.filter.toLowerCase();

		return this.historyItems.filter(
			historyItem =>
				historyItem.details &&
				historyItem.details.toLowerCase().indexOf(filterLower) > -1
		);
	}

	private get tableFields(): VueBootstrapField[] {
		let fields: VueBootstrapField[] = [];
		fields.push({ key: "created", label: "Created" });
		fields.push({ key: "eventId", label: "Event ID", thClass: "text-nowrap" });
		fields.push({ key: "details", label: "Details" });
		fields.push({ key: "eventOutcome", label: "Outcome" });

		return fields;
	}

	private get monthSelectOptions(): { label: string, value: number }[] {
		const now = new Date();

		return Array.from({ length: this.monthSelectOptionsCount }, (_, offset) => {
			const optionDate = new Date(now.getFullYear(), now.getMonth() - offset, 1);

			return {
				label: optionDate.toLocaleString("default", { month: "long", year: "numeric" }),
				value: offset
			};
		});
	}

	@Watch("response")
	private async onResponseChanged(value: Response | null): Promise<void> {
		this.updateResults(value);
	}

	@Watch("selectedPage")
	private async onSelectedPageChanged() {
		this.updateResults(this.response);
	}

	@Watch("monthOffset")
	private async onMonthOffsetChanged() {
		if (this.selectedPage !== 1) {
			this.selectedPage = 1;
		} else {
			this.updateResults(this.response);
		}
	}

	@Watch("filter")
	private onFilterChanged(): void {
		if (!this.isPaginatedResultsFeatureEnabled) {
			return;
		}

		this.handleFilterChanged();
	}

	private async fetchAlarmPointHistory(response: Response) {
		const cancelTokenSource = getNewCancelTokenSource(this.historyCancelTokenSource, true, this.cancelPreviousHistoryMessage);
		this.historyCancelTokenSource = cancelTokenSource;

		this.fetchingHistory = true;

		try {
			if (this.isPaginatedResultsFeatureEnabled) {
				const apiResponse = await api.responseHistoryPaginated(response.responseID, this.filter, this.selectedPage, this.monthOffset, cancelTokenSource.token);
				if (apiResponse !== null) {
					this.historyItems = apiResponse.data;
					this.totalAvailableRows = apiResponse.totalRecords;
				}
			} else {
				const apiResponse = await api.responseHistory(response.responseID, cancelTokenSource.token);
				if (apiResponse !== null) {
					this.historyItems = apiResponse;
					this.totalAvailableRows = apiResponse.length;
				}
			}
		} finally {
			if (this.historyCancelTokenSource === cancelTokenSource) {
				this.fetchingHistory = false;
			}
		}
	}

	private async updateResults(response: Response) {
		if (response) {
			this.fetchAlarmPointHistory(response);
		}
		else
		{
			this.historyItems = [];
		}
	}
}
